import * as React from "react";
import {
	Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../theme";

const Section = ({ title, children }) => (
	<Box sx={{ mb: 3 }}>
		<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
			{title}
		</Typography>
		{children}
	</Box>
);

const BulletList = ({ items }) => (
	<Box component="ul" sx={{ m: 0, pl: 2.5 }}>
		{items.map((item) => (
			<Typography key={item} component="li" variant="body2" sx={{ mb: 0.75, color: "text.secondary" }}>
				{item}
			</Typography>
		))}
	</Box>
);

export default function ImageGuidelinesDialog({ open, onClose }) {
	return (
		<ThemeProvider theme={theme}>
			<Dialog
				open={open}
				onClose={onClose}
				maxWidth="sm"
				fullWidth
				scroll="paper"
				sx={{ zIndex: 1500 }}
				PaperProps={{ sx: { bgcolor: "#fff" } }}
			>
				<DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
					Image guidelines
				</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
						Buyers judge the product from these photos. The primary image is required.
						Extra images are optional. Any crop or orientation is fine.
					</Typography>

					<Section title="Requirements">
						<BulletList items={[
							"Up to 5 images per product and per variant.",
							"Primary (first slot) is required. It is the storefront lead image. A listing can save with only this photo.",
							"The other four slots are optional extra views.",
							"Maximum 2 MB per file. Larger files are rejected.",
							"JPEG / JPG, PNG, WebP, or GIF. Use JPEG for photos. Do not upload PDF, BMP, TIFF, HEIC, or video.",
							"Any aspect ratio is fine. There is no square or pixel-size rule.",
						]} />
					</Section>

					<Divider sx={{ mb: 3 }} />

					<Section title="Primary image">
						<BulletList items={[
							"Sharp photo of the actual item you sell.",
							"Plain white or light background. Product centred and easy to see.",
							"One product per frame. Same colour as the listing.",
						]} />
					</Section>

					<Section title="Extra images (optional)">
						<BulletList items={[
							"Other angles, a close-up of texture or label, scale (in hand or next to a known object), or one lifestyle shot in use.",
							"Keep the same lighting as the primary.",
							"Skip these if you only have one good shot.",
						]} />
					</Section>

					<Section title="Do not">
						<BulletList items={[
							"Watermarks, shop name, price, sale badges, collages, borders, or stock-site logos.",
							"Blurry, dark, over-filtered, or screenshot-of-a-screenshot photos.",
							"Mock-ups that hide the real item. Mannequins only if the garment is the product.",
							"Text overlays or infographic walls on the primary image. One extra image may have readable text on a phone.",
						]} />
					</Section>

					<Section title="Variant images">
						<BulletList items={[
							"Same 5-slot, 2 MB, and format rules.",
							"Primary is required and should show that colour or size, not a different SKU.",
							"Extra slots are optional.",
						]} />
					</Section>

					<Box sx={{ bgcolor: "grey.50", border: "1px solid", borderColor: "grey.200", borderRadius: 1, p: 2 }}>
						<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
							Checklist
						</Typography>
						<BulletList items={[
							"Primary photo attached, under 2 MB.",
							"Actual item, clear, light background.",
							"No watermark or badge on the primary.",
							"Extra photos only if they add a new view.",
						]} />
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose} color="secondary" variant="contained">
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</ThemeProvider>
	);
}
